/**
 * XML Tester Service
 * Handles XPath query testing, XML validation, and formatting
 */

import { DOMParser } from '@xmldom/xmldom';
import * as xpath from 'xpath';
import { EncodingOperation, EncoderToolType } from '../types/EncodingOperation';
import { ValidationError, ProcessingError } from '../utils/errorHandler';

export type XMLOperation = 'xpath' | 'validate' | 'format' | 'minify';

export interface XPathResult {
  results: Array<{
    type: string;
    value: string;
    nodeName?: string;
    nodeValue?: string;
    attributes?: Record<string, string>;
  }>;
  resultCount: number;
  hasResults: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Array<{
    line?: number;
    column?: number;
    message: string;
  }>;
}

export interface ExampleXPath {
  name: string;
  query: string;
  description: string;
}

export class XMLTesterService {
  private static operationCounter = 0;

  /**
   * Execute XPath query against XML
   */
  static async executeXPath(
    xmlData: string,
    xpathQuery: string
  ): Promise<EncodingOperation> {
    const operationId = `xml-tester-${Date.now()}-${++this.operationCounter}`;

    try {
      // Validate inputs
      this.validateInput(xmlData, 'XML data');
      this.validateInput(xpathQuery, 'XPath query');

      // Parse XML
      const doc = this.parseXML(xmlData);

      // Execute XPath
      let xpathResult: any;
      try {
        xpathResult = xpath.select(xpathQuery, doc);
      } catch (error) {
        throw new ValidationError(
          `Invalid XPath query: ${error instanceof Error ? error.message : 'Query error'}`,
          EncoderToolType.XML_TESTER
        );
      }

      // Process results
      const result = this.processXPathResult(xpathResult);
      const outputJson = JSON.stringify(result, null, 2);

      return {
        id: operationId,
        toolType: EncoderToolType.XML_TESTER,
        operationType: 'encode',
        inputData: xmlData,
        outputData: outputJson,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          operation: 'xpath',
          query: xpathQuery,
          resultCount: result.resultCount,
          hasResults: result.hasResults,
          inputLength: xmlData.length,
          outputLength: outputJson.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        error instanceof Error ? error.message : 'XML operation failed',
        EncoderToolType.XML_TESTER
      );
    }
  }

  /**
   * Validate XML document (with optional XSD schema)
   */
  static async validate(xmlData: string, xsdSchema?: string): Promise<EncodingOperation> {
    const operationId = `xml-tester-${Date.now()}-${++this.operationCounter}`;

    try {
      this.validateInput(xmlData, 'XML data');

      const errors: Array<{ line?: number; column?: number; message: string }> = [];

      // Parse XML document
      const parser = new DOMParser({
        errorHandler: {
          warning: (msg: string) => {
            errors.push({ message: `Warning: ${msg}` });
          },
          error: (msg: string) => {
            errors.push({ message: `Error: ${msg}` });
          },
          fatalError: (msg: string) => {
            errors.push({ message: `Fatal Error: ${msg}` });
          }
        }
      });

      const xmlDoc = parser.parseFromString(xmlData, 'text/xml');

      // If XSD schema provided, validate against it
      if (xsdSchema && xsdSchema.trim()) {
        try {
          const schemaDoc = this.parseXML(xsdSchema);
          const validationErrors = this.validateAgainstXSD(xmlDoc, schemaDoc);
          errors.push(...validationErrors);
        } catch (schemaError) {
          errors.push({
            message: `Schema Error: ${schemaError instanceof Error ? schemaError.message : 'Invalid XSD schema'}`
          });
        }
      }

      const result: ValidationResult = {
        isValid: errors.length === 0,
        errors
      };

      const outputJson = JSON.stringify(result, null, 2);

      return {
        id: operationId,
        toolType: EncoderToolType.XML_TESTER,
        operationType: 'encode',
        inputData: xmlData,
        outputData: outputJson,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          operation: 'validate',
          isValid: result.isValid,
          errorCount: errors.length,
          hasXSD: !!xsdSchema,
          inputLength: xmlData.length,
          outputLength: outputJson.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        error instanceof Error ? error.message : 'XML operation failed',
        EncoderToolType.XML_TESTER
      );
    }
  }

  /**
   * Format (pretty print) XML
   */
  static async format(xmlData: string): Promise<EncodingOperation> {
    const operationId = `xml-tester-${Date.now()}-${++this.operationCounter}`;

    try {
      this.validateInput(xmlData, 'XML data');

      // Parse and format
      const doc = this.parseXML(xmlData);
      const formatted = this.formatXML(doc, 0);

      return {
        id: operationId,
        toolType: EncoderToolType.XML_TESTER,
        operationType: 'encode',
        inputData: xmlData,
        outputData: formatted,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          operation: 'format',
          inputLength: xmlData.length,
          outputLength: formatted.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        error instanceof Error ? error.message : 'XML operation failed',
        EncoderToolType.XML_TESTER
      );
    }
  }

  /**
   * Minify XML
   */
  static async minify(xmlData: string): Promise<EncodingOperation> {
    const operationId = `xml-tester-${Date.now()}-${++this.operationCounter}`;

    try {
      this.validateInput(xmlData, 'XML data');

      // Parse and minify
      const doc = this.parseXML(xmlData);
      const minified = this.minifyXML(doc);

      return {
        id: operationId,
        toolType: EncoderToolType.XML_TESTER,
        operationType: 'encode',
        inputData: xmlData,
        outputData: minified,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          operation: 'minify',
          inputLength: xmlData.length,
          outputLength: minified.length,
          compressionRatio: `${(((xmlData.length - minified.length) / xmlData.length) * 100).toFixed(1)}%`
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        error instanceof Error ? error.message : 'XML operation failed',
        EncoderToolType.XML_TESTER
      );
    }
  }

  /**
   * Parse XML string to document
   */
  private static parseXML(xmlData: string): Document {
    const errors: string[] = [];

    const parser = new DOMParser({
      errorHandler: {
        warning: () => {},
        error: (msg: string) => {
          errors.push(msg);
        },
        fatalError: (msg: string) => {
          errors.push(msg);
        }
      }
    });

    const doc = parser.parseFromString(xmlData, 'text/xml');

    if (errors.length > 0) {
      throw new ValidationError(`Invalid XML: ${errors[0]}`, EncoderToolType.XML_TESTER);
    }

    return doc;
  }

  /**
   * Validate XML against XSD schema
   * Note: This is a basic structural validation since JavaScript doesn't have full XSD support
   */
  private static validateAgainstXSD(xmlDoc: Document, schemaDoc: Document): Array<{ message: string }> {
    const errors: Array<{ message: string }> = [];

    try {
      // Get root element from schema
      const schemaRoot = schemaDoc.documentElement;
      if (!schemaRoot || (schemaRoot.nodeName !== 'xs:schema' && schemaRoot.nodeName !== 'xsd:schema')) {
        errors.push({ message: 'Schema Error: root element must be xs:schema or xsd:schema' });
        return errors;
      }

      // Get all element definitions from schema
      const schemaElements = this.getSchemaElements(schemaDoc);

      // Get root element from XML
      const xmlRoot = xmlDoc.documentElement;
      if (!xmlRoot) {
        errors.push({ message: 'XML document has no root element' });
        return errors;
      }

      // Validate XML structure against schema
      this.validateElementAgainstSchema(xmlRoot, schemaElements, errors);

    } catch (error) {
      errors.push({
        message: `XSD Validation Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }

    return errors;
  }

  /**
   * Get element definitions from XSD schema
   */
  private static getSchemaElements(schemaDoc: Document): Map<string, any> {
    const elements = new Map<string, any>();
    const complexTypes = new Map<string, any>();

    // First, collect all complexType definitions
    const complexTypeNodes = xpath.select('//*[local-name()="complexType"][@name]', schemaDoc) as Node[];
    complexTypeNodes.forEach((node: any) => {
      const name = node.getAttribute('name');
      if (name) {
        complexTypes.set(name, node);
      }
    });

    // Get all xs:element or xsd:element nodes
    const elementNodes = xpath.select('//*[local-name()="element"]', schemaDoc) as Node[];

    elementNodes.forEach((node: any) => {
      const name = node.getAttribute('name');
      const type = node.getAttribute('type');
      const minOccurs = node.getAttribute('minOccurs') || '1';
      const maxOccurs = node.getAttribute('maxOccurs') || '1';

      if (name) {
        // Get the complexType node if type references one
        let complexTypeNode = null;
        if (type && complexTypes.has(type)) {
          complexTypeNode = complexTypes.get(type);
        }

        elements.set(name, {
          type,
          minOccurs,
          maxOccurs,
          node,
          complexTypeNode
        });
      }
    });

    return elements;
  }

  /**
   * Validate XML element against schema definition
   */
  private static validateElementAgainstSchema(
    element: Element,
    schemaElements: Map<string, any>,
    errors: Array<{ message: string }>
  ): void {
    const elementName = element.nodeName;
    const schemaDef = schemaElements.get(elementName);

    if (!schemaDef) {
      // Element is not defined in the schema
      errors.push({
        message: `Element '${elementName}' is not defined in the schema`
      });
      return;
    }

    // Validate required attributes (basic check)
    const requiredAttrs = this.getRequiredAttributes(schemaDef);
    requiredAttrs.forEach(attrName => {
      if (!element.hasAttribute(attrName)) {
        errors.push({
          message: `Element '${elementName}' is missing required attribute '${attrName}'`
        });
      }
    });

    // Validate child elements recursively
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i];
      if (child.nodeType === 1) { // Element node
        this.validateElementAgainstSchema(child as Element, schemaElements, errors);
      }
    }
  }

  /**
   * Get required attributes from schema element definition
   */
  private static getRequiredAttributes(schemaDef: any): string[] {
    const required: string[] = [];

    try {
      // Check both the element node and the complexType node if it exists
      const nodesToCheck = [schemaDef.node];
      if (schemaDef.complexTypeNode) {
        nodesToCheck.push(schemaDef.complexTypeNode);
      }

      nodesToCheck.forEach(node => {
        const attributes = xpath.select('.//*[local-name()="attribute"]', node) as Node[];
        attributes.forEach((attr: any) => {
          const use = attr.getAttribute('use');
          const name = attr.getAttribute('name');
          if (use === 'required' && name) {
            required.push(name);
          }
        });
      });
    } catch (error) {
      // Ignore errors in attribute extraction
    }

    return required;
  }

  /**
   * Process XPath query result
   */
  private static processXPathResult(xpathResult: any): XPathResult {
    const results: XPathResult['results'] = [];

    if (Array.isArray(xpathResult)) {
      xpathResult.forEach((node: any) => {
        if (node.nodeType === 1) {
          // Element node
          const attributes: Record<string, string> = {};
          if (node.attributes) {
            for (let i = 0; i < node.attributes.length; i++) {
              const attr = node.attributes[i];
              attributes[attr.name] = attr.value;
            }
          }

          results.push({
            type: 'element',
            value: node.textContent || '',
            nodeName: node.nodeName,
            attributes: Object.keys(attributes).length > 0 ? attributes : undefined
          });
        } else if (node.nodeType === 2) {
          // Attribute node
          results.push({
            type: 'attribute',
            value: node.value || '',
            nodeName: node.name
          });
        } else if (node.nodeType === 3) {
          // Text node
          results.push({
            type: 'text',
            value: node.nodeValue || ''
          });
        } else {
          results.push({
            type: 'node',
            value: node.toString()
          });
        }
      });
    } else if (typeof xpathResult === 'string' || typeof xpathResult === 'number' || typeof xpathResult === 'boolean') {
      results.push({
        type: typeof xpathResult,
        value: xpathResult.toString()
      });
    }

    return {
      results,
      resultCount: results.length,
      hasResults: results.length > 0
    };
  }

  /**
   * Format XML with indentation
   */
  private static formatXML(node: Node, indent: number): string {
    const INDENT = '  ';
    let formatted = '';

    if (node.nodeType === 1) {
      // Element node
      const element = node as Element;
      const indentStr = INDENT.repeat(indent);

      formatted += `${indentStr}<${element.nodeName}`;

      // Add attributes
      if (element.attributes) {
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          formatted += ` ${attr.name}="${attr.value}"`;
        }
      }

      if (element.childNodes.length === 0) {
        formatted += ' />\n';
      } else {
        formatted += '>\n';

        // Process children
        for (let i = 0; i < element.childNodes.length; i++) {
          formatted += this.formatXML(element.childNodes[i], indent + 1);
        }

        formatted += `${indentStr}</${element.nodeName}>\n`;
      }
    } else if (node.nodeType === 3) {
      // Text node
      const text = node.nodeValue?.trim();
      if (text) {
        formatted += `${INDENT.repeat(indent)}${text}\n`;
      }
    } else if (node.nodeType === 9) {
      // Document node
      const doc = node as Document;
      if (doc.documentElement) {
        formatted = this.formatXML(doc.documentElement, 0);
      }
    }

    return formatted;
  }

  /**
   * Minify XML by removing whitespace
   */
  private static minifyXML(node: Node): string {
    let minified = '';

    if (node.nodeType === 1) {
      // Element node
      const element = node as Element;
      minified += `<${element.nodeName}`;

      // Add attributes
      if (element.attributes) {
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          minified += ` ${attr.name}="${attr.value}"`;
        }
      }

      if (element.childNodes.length === 0) {
        minified += '/>';
      } else {
        minified += '>';

        // Process children
        for (let i = 0; i < element.childNodes.length; i++) {
          minified += this.minifyXML(element.childNodes[i]);
        }

        minified += `</${element.nodeName}>`;
      }
    } else if (node.nodeType === 3) {
      // Text node - preserve non-whitespace text
      const text = node.nodeValue?.trim();
      if (text) {
        minified += text;
      }
    } else if (node.nodeType === 9) {
      // Document node
      const doc = node as Document;
      if (doc.documentElement) {
        minified = this.minifyXML(doc.documentElement);
      }
    }

    return minified;
  }

  /**
   * Validate input
   */
  private static validateInput(data: string, fieldName: string): void {
    if (!data || data.trim() === '') {
      throw new ValidationError(`${fieldName} cannot be empty`, EncoderToolType.XML_TESTER);
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (data.length > maxSize) {
      throw new ValidationError(
        `${fieldName} exceeds maximum size of ${maxSize / (1024 * 1024)}MB`,
        EncoderToolType.XML_TESTER
      );
    }
  }

  /**
   * Get example XPath queries
   */
  static getExampleXPaths(): ExampleXPath[] {
    return [
      {
        name: 'Root Element',
        query: '/',
        description: 'Select the document root'
      },
      {
        name: 'All Elements',
        query: '//*',
        description: 'Select all elements'
      },
      {
        name: 'Specific Element',
        query: '//book',
        description: 'Select all book elements'
      },
      {
        name: 'Element by Position',
        query: '//book[1]',
        description: 'Select the first book'
      },
      {
        name: 'Attribute',
        query: '//book/@id',
        description: 'Select id attributes of books'
      },
      {
        name: 'Attribute Value',
        query: '//book[@category="fiction"]',
        description: 'Select fiction books'
      },
      {
        name: 'Text Content',
        query: '//book/title/text()',
        description: 'Select book title text'
      },
      {
        name: 'Child Elements',
        query: '//book/author',
        description: 'Select all author elements within books'
      },
      {
        name: 'Descendant',
        query: '//store//price',
        description: 'Select all price elements under store'
      },
      {
        name: 'Conditional',
        query: '//book[price < 10]',
        description: 'Select books with price less than 10'
      },
      {
        name: 'Multiple Conditions',
        query: '//book[@category="fiction" and price < 10]',
        description: 'Select fiction books under $10'
      },
      {
        name: 'Count',
        query: 'count(//book)',
        description: 'Count number of books'
      },
      {
        name: 'Contains',
        query: '//book[contains(title, "Lord")]',
        description: 'Books with "Lord" in title'
      },
      {
        name: 'Last Element',
        query: '//book[last()]',
        description: 'Select the last book'
      },
      {
        name: 'Parent',
        query: '//title/..',
        description: 'Select parent elements of title'
      }
    ];
  }

  /**
   * Get sample XML data
   */
  static getSampleXML(): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<store>
  <book id="1" category="reference">
    <title>Sayings of the Century</title>
    <author>Nigel Rees</author>
    <price>8.95</price>
  </book>
  <book id="2" category="fiction">
    <title>Sword of Honour</title>
    <author>Evelyn Waugh</author>
    <price>12.99</price>
  </book>
  <book id="3" category="fiction">
    <title>Moby Dick</title>
    <author>Herman Melville</author>
    <isbn>0-553-21311-3</isbn>
    <price>8.99</price>
  </book>
  <book id="4" category="fiction">
    <title>The Lord of the Rings</title>
    <author>J. R. R. Tolkien</author>
    <isbn>0-395-19395-8</isbn>
    <price>22.99</price>
  </book>
  <bicycle>
    <color>red</color>
    <price>19.95</price>
  </bicycle>
</store>`;
  }

  /**
   * Get sample XSD schema
   */
  static getSampleXSD(): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema">
  <xs:element name="store">
    <xs:complexType>
      <xs:sequence>
        <xs:element name="book" type="BookType" minOccurs="0" maxOccurs="unbounded"/>
        <xs:element name="bicycle" type="BicycleType" minOccurs="0" maxOccurs="1"/>
      </xs:sequence>
    </xs:complexType>
  </xs:element>

  <xs:complexType name="BookType">
    <xs:sequence>
      <xs:element name="title" type="xs:string"/>
      <xs:element name="author" type="xs:string"/>
      <xs:element name="isbn" type="xs:string" minOccurs="0"/>
      <xs:element name="price" type="xs:decimal"/>
    </xs:sequence>
    <xs:attribute name="id" type="xs:string" use="required"/>
    <xs:attribute name="category" type="xs:string" use="required"/>
  </xs:complexType>

  <xs:complexType name="BicycleType">
    <xs:sequence>
      <xs:element name="color" type="xs:string"/>
      <xs:element name="price" type="xs:decimal"/>
    </xs:sequence>
  </xs:complexType>
</xs:schema>`;
  }

  /**
   * Clear cache (for testing)
   */
  static clearCache(): void {
    this.operationCounter = 0;
  }
}
